import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { FormErrors, useFormState } from '../../hooks/useFormState';
import { isBlank, isValidEmail, isValidLinkedInUrl, validateCvFile } from '../../utils/validation';
import { buttonClass } from '../../utils/styles';
import { experienceRanges, industries, roleTypes } from '../../data/formOptions';
import { candidateDisclaimer } from '../../data/audiences';
import { TextField } from '../form/TextField';
import { SelectField } from '../form/SelectField';
import { FileField } from '../form/FileField';
import { CheckboxField } from '../form/CheckboxField';
import { FormErrorSummary } from '../form/FormErrorSummary';
import { FormPreviewNotice } from '../form/FormPreviewNotice';
import { FormValidatedPanel } from '../form/FormValidatedPanel';
type CandidateValues = {
  fullName: string;
  email: string;
  currentRole: string;
  industry: string;
  experience: string;
  roleType: string;
  linkedin: string;
  cv: File | null;
  consent: boolean;
};
const initialValues: CandidateValues = {
  fullName: '',
  email: '',
  currentRole: '',
  industry: '',
  experience: '',
  roleType: '',
  linkedin: '',
  cv: null,
  consent: false
};
type CandidateRegistrationFormProps = {
  formId?: string;
};
export function CandidateRegistrationForm({
  formId = 'candidate-form'
}: CandidateRegistrationFormProps) {
  const {
    values,
    errors,
    status,
    fieldId,
    setField,
    handleSubmit,
    edit,
    reset
  } = useFormState(formId, initialValues, validate);
  if (status === 'validated') {
    return <FormValidatedPanel formId={formId} title="Your registration is complete" message={`Thank you, ${values.fullName.trim().split(' ')[0]}. Your details have passed all checks and are ready to send.${values.cv ? ' Your CV was checked for format and size only — it has not been uploaded.' : ''}`} onEdit={edit} onReset={reset} data-id="element-93" data-mp="268ddyhxr8a9t" />;
  }
  return <form noValidate onSubmit={handleSubmit} className="space-y-6" aria-label="Candidate registration" data-id="element-94" data-mp="1mjaqilg7a9n0">
      {status === 'invalid' && <FormErrorSummary count={Object.keys(errors).length} data-id="element-95" data-mp="1hnn3abla3qk5" />}
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-96" data-mp="1vk8yfetrf1av">
        <TextField id={fieldId('fullName')} label="Full name" required autoComplete="name" value={values.fullName} onChange={v => setField('fullName', v)} error={errors.fullName} data-id="element-97" data-mp="1vixr5513vbf0" />
        <TextField id={fieldId('email')} label="Professional email" type="email" required autoComplete="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} data-id="element-98" data-mp="ywm5hqvwa126" />
        <TextField id={fieldId('currentRole')} label="Current role" required autoComplete="organization-title" value={values.currentRole} onChange={v => setField('currentRole', v)} error={errors.currentRole} data-id="element-99" data-mp="k53hb5svt07q" />
        <SelectField id={fieldId('industry')} label="Industry" required options={industries} value={values.industry} onChange={v => setField('industry', v)} error={errors.industry} data-id="element-100" data-mp="2vldpiqhfbd06" />
        <SelectField id={fieldId('experience')} label="Years of experience" required options={experienceRanges} value={values.experience} onChange={v => setField('experience', v)} error={errors.experience} data-id="element-101" data-mp="2mqfy5ntxc8ay" />
        <SelectField id={fieldId('roleType')} label="Preferred opportunity or role type" required options={roleTypes} value={values.roleType} onChange={v => setField('roleType', v)} error={errors.roleType} data-id="element-102" data-mp="30mb7b1yiu515" />
      </div>
      <TextField id={fieldId('linkedin')} label="LinkedIn profile URL" type="url" required autoComplete="url" placeholder="https://www.linkedin.com/in/your-name" value={values.linkedin} onChange={v => setField('linkedin', v)} error={errors.linkedin} data-id="element-103" data-mp="1u21w8wzpsru4" />
      <FileField id={fieldId('cv')} label="CV or résumé" file={values.cv} onChange={file => setField('cv', file)} error={errors.cv} data-id="element-104" data-mp="1vmwrz7k9l962" />
      <CheckboxField id={fieldId('consent')} checked={values.consent} onChange={v => setField('consent', v)} error={errors.consent} data-id="element-105" data-mp="2ob2d3vf8d06f">
        I have read the{' '}
        <Link to="/privacy-policy" className="font-medium text-navy underline underline-offset-2" data-id="element-106" data-mp="2ecuvrbh14g3w">
          privacy notice
        </Link>{' '}
        and consent to Avenqor Talent Search holding and processing my information to consider me for relevant
        opportunities. I understand I can withdraw consent at any time.
      </CheckboxField>
      <p className="text-[13px] leading-relaxed text-muted" data-id="element-107" data-mp="sv3n7xd5j85s">{candidateDisclaimer}</p>
      <div className="flex flex-col gap-5 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-108" data-mp="xtx5e33dvnca">
        <FormPreviewNotice data-id="element-109" data-mp="12qdt4w6o7zou" />
        <button type="submit" className={buttonClass('navy', 'shrink-0')} data-id="element-110" data-mp="12q41l45z9kh1">
          Register Interest
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-111" data-mp="17ki37ob8gnu5" />
        </button>
      </div>
    </form>;
}
function validate(v: CandidateValues): FormErrors<CandidateValues> {
  const e: FormErrors<CandidateValues> = {};
  if (isBlank(v.fullName)) e.fullName = 'Please enter your full name.';
  if (isBlank(v.email)) e.email = 'Please enter your professional email.';else if (!isValidEmail(v.email)) e.email = 'Please enter a valid email address.';
  if (isBlank(v.currentRole)) e.currentRole = 'Please enter your current role.';
  if (!v.industry) e.industry = 'Please select an industry.';
  if (!v.experience) e.experience = 'Please select your years of experience.';
  if (!v.roleType) e.roleType = 'Please select a preferred role type.';
  if (isBlank(v.linkedin)) e.linkedin = 'Please enter your LinkedIn profile URL.';else if (!isValidLinkedInUrl(v.linkedin)) e.linkedin = 'Please enter a full profile URL, e.g. https://www.linkedin.com/in/your-name.';
  const cvError = validateCvFile(v.cv);
  if (cvError) e.cv = cvError;
  if (!v.consent) e.consent = 'Please confirm your consent to continue.';
  return e;
}